import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../../Context/Createcontext";
import addToCartIcon from "../../Icons/addToCartIcon.svg";
import { toast } from "react-hot-toast";
import { api_url } from "../../config/config";
import wish from "../../images/wish.svg";
import wishlist from "../../images/wishlist_nofill.svg";
import { useWishlist } from "../../Context/WishlistContext";
import Popup from "../common/Popup";

const AllProducts_home = ({ products, handleclickforfeaturedproduct }) => {
  const {
    handleAddToCart,
    showLoginPopup,
    setShowLoginPopup,
    handleRemoveFromCart,
    cartItems,
    setCartItems,
  } = useCart();
  const {
    wishlist: wishlistItems,
    addToWishlist,
    removeFromWishlist,
    loading: wishlistLoading,
  } = useWishlist();
  const [tooltipOpen, setTooltipOpen] = useState(null);

  /* ── Cart helpers ── */
  const getCartQuantity = (variantId) => {
    const item = cartItems.find((ci) => ci.variant_id === variantId);
    return item ? item.quantity : 0;
  };

  const OutOfQuantityCheck = (variants) =>
    variants.every((v) => !v.quantity || v.quantity === 0);

  const handleIncrease = async (event, variant, item) => {
    event.preventDefault();
    if (!variant?.variant_id) return;
    const quantity = getCartQuantity(variant.variant_id);
    const res = await fetch(`${api_url}/cart`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        product_id: variant.product_id,
        quantity: quantity + 1,
        variant_id: variant.variant_id,
      }),
    });
    const data = await res.json();
    if (
      !res.ok ||
      data.message?.toLowerCase().includes("stock not available")
    ) {
      toast.error(`${item.name} is out of stock`);
      return;
    }
    setCartItems((prev) =>
      prev.map((ci) =>
        ci.variant_id === variant.variant_id
          ? { ...ci, quantity: ci.quantity + 1 }
          : ci,
      ),
    );
  };

  const handleDecrease = async (event, variant) => {
    event.preventDefault();
    if (!variant?.variant_id) return;
    const quantity = getCartQuantity(variant.variant_id);
    if (quantity === 1) {
      await handleRemoveFromCart(variant.product_id, variant.variant_id);
      setCartItems((prev) =>
        prev.filter((ci) => ci.variant_id !== variant.variant_id),
      );
      return;
    }
    const res = await fetch(`${api_url}/cart`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        product_id: variant.product_id,
        quantity: quantity - 1,
        variant_id: variant.variant_id,
      }),
    });
    const data = await res.json();
    if (
      !res.ok ||
      data.message?.toLowerCase().includes("stock not available")
    ) {
      toast.error("Something went wrong");
      return;
    }
    setCartItems((prev) =>
      prev.map((ci) =>
        ci.variant_id === variant.variant_id
          ? { ...ci, quantity: ci.quantity - 1 }
          : ci,
      ),
    );
  };

  /* ─────────────────────────────────────────────────────────
     PRICE LOGIC
     API shape per variant:
       variant.mrp              → original price  (string | null)
       variant.discounted_price → sale price       (number | null)
       product.discount_percent → % badge          (number | null)

     Rules:
     1. mrp is the base price
     2. If discounted_price is a valid number > 0 AND < mrp → show strikethrough mrp + discounted price + % badge
     3. Otherwise → show only mrp, no strikethrough, no badge
  ───────────────────────────────────────────────────────── */
  const getPriceInfo = (variants, discountPercent) => {
    if (!variants || variants.length === 0)
      return {
        mrp: null,
        discountedPrice: null,
        showDiscount: false,
        discountPct: 0,
      };

    // Find variant with lowest mrp
    const sorted = [...variants]
      .map((v) => ({
        ...v,
        _mrp: parseFloat(v.mrp) || 0,
        _disc: parseFloat(v.discounted_price) || 0,
      }))
      .filter((v) => v._mrp > 0)
      .sort((a, b) => a._mrp - b._mrp);

    if (sorted.length === 0)
      return {
        mrp: null,
        discountedPrice: null,
        showDiscount: false,
        discountPct: 0,
      };

    const best = sorted[0];
    const mrp = best._mrp;
    const discPrice = best._disc;

    // Only show discount if discounted_price is valid, > 0, and strictly less than mrp
    const showDiscount = discPrice > 0 && discPrice < mrp;
    const finalPrice = showDiscount ? discPrice : mrp;
    const pct = showDiscount
      ? discountPercent || Math.round(((mrp - discPrice) / mrp) * 100)
      : 0;

    return {
      mrp,
      discountedPrice: finalPrice,
      showDiscount,
      discountPct: pct,
    };
  };

  // "Our Collection" should only ever surface products a customer can
  // actually buy — filter out-of-stock items before slicing to the first 12
  // rather than after, so a page full of OOS products doesn't leave the
  // section looking sparse or empty.
  const inStockProducts = products.filter(
    (product) => !OutOfQuantityCheck(product.variant || []),
  );
  const displayed = inStockProducts.slice(0, 12);

  return (
    <section className="w-full py-4 sm:py-6 lg:py-6 px-4 sm:px-6 lg:px-6 xl:px-6 font-poppins bg-[#f8fafa]">
      {/* ── Section header ── */}
      <div className="flex items-end justify-between mb-6 sm:mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-5 h-[2px] bg-[#ff601e] rounded-full" />
            <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-widest text-[#ff601e]">
              Our Collection
            </span>
          </div>
          <h2 className="font-bold text-[20px] sm:text-[24px] lg:text-[28px] text-[#1e4a46] leading-tight">
            All Medicines
          </h2>
          <p className="text-[13px] text-[#8faba9] mt-1 hidden sm:block">
            Explore our full range of medicines, devices &amp; wellness
            essentials
          </p>
        </div>

        <Link to="/products">
          <button className="flex items-center gap-1.5 bg-[#162c2a] hover:bg-[#ff601e] text-white font-semibold text-[11px] sm:text-[12px] px-4 h-8 rounded-xl transition-all duration-200 shadow-[0_2px_8px_rgba(22,44,42,0.25)] hover:shadow-[0_4px_14px_rgba(255,96,30,0.4)] hover:-translate-y-0.5">
            View All
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </Link>
      </div>

      {/* ── Product grid ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-5">
        {displayed.map((product, idx) => {
          const variants = product.variant || [];
          const variant = variants[0];
          const isOutOfStock = OutOfQuantityCheck(variants);
          const cartQty = getCartQuantity(variant?.variant_id);
          const isMultiVariant = variants.length > 1;
          const isWishlisted = wishlistItems.some(
            (w) =>
              w.product_id === product.product_id &&
              w.variant_id === variant?.variant_id,
          );

          /* ── price ── */
          const { mrp, discountedPrice, showDiscount, discountPct } =
            getPriceInfo(variants, product.discount_percent);

          /* ── alternating card theme ── */
          const isOrange = idx % 2 === 1;
          const accentBorder = isOrange
            ? "hover:border-[#ff601e]/40"
            : "hover:border-[#5fbdb3]";
          const accentGrad = isOrange
            ? "from-[#ff601e] to-[#e55519]"
            : "from-[#1e4a46] via-[#5fbdb3] to-[#1e4a46]";
          const imgBg = isOrange ? "bg-[#fff8f5]" : "bg-[#f4fbfa]";
          const priceBadge = isOrange ? "text-[#ff601e]" : "text-[#5fbdb3]";
          const btnCls = isOrange
            ? "bg-[#ff601e] hover:bg-[#e55519] shadow-[0_2px_8px_rgba(255,96,30,0.3)] hover:shadow-[0_4px_14px_rgba(255,96,30,0.5)]"
            : "bg-[#1e4a46] hover:bg-[#ff601e] shadow-[0_2px_8px_rgba(22,44,42,0.2)] hover:shadow-[0_4px_14px_rgba(255,96,30,0.4)]";

          return (
            <div
              key={product.product_id}
              className={`group bg-white border-[1.5px] border-[#e2eaea] ${accentBorder} rounded-2xl overflow-hidden
                shadow-[0_2px_14px_rgba(22,44,42,0.07)] hover:shadow-[0_10px_36px_rgba(22,44,42,0.16)]
                hover:-translate-y-2 transition-all duration-300 flex flex-col relative`}
              style={{
                animation: `fadeInUp 0.35s ease ${(idx % 6) * 40}ms both`,
              }}
            >
              {/* Top gradient accent */}
              <div
                className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${accentGrad} opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10`}
              />

              {/* Discount badge — only shown when there's a real discount */}
              {showDiscount && discountPct > 0 && (
                <div className="absolute top-3 right-3 bg-[#ff601e] text-white text-[9px] sm:text-[10px] font-bold w-9 h-9 sm:w-10 sm:h-10 rounded-full flex flex-col items-center justify-center shadow-[0_2px_8px_rgba(255,96,30,0.45)] z-10 leading-tight text-center">
                  {discountPct}%<br />
                  OFF
                </div>
              )}

              {/* Wishlist btn */}
              <button
                className="absolute top-3 left-3 z-10 w-7 h-7 sm:w-8 sm:h-8 bg-white rounded-xl border border-[#e2eaea] flex items-center justify-center shadow-sm hover:border-[#5fbdb3] hover:bg-[#eaf4f3] transition-all duration-200"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!localStorage.getItem("authToken")) {
                    setShowLoginPopup(true);
                    return;
                  }
                  isWishlisted
                    ? removeFromWishlist(
                        product.product_id,
                        variant?.variant_id,
                      )
                    : addToWishlist(product, variant);
                }}
                disabled={wishlistLoading}
                onMouseEnter={() => setTooltipOpen(idx)}
                onMouseLeave={() => setTooltipOpen(null)}
              >
                <img
                  src={isWishlisted ? wish : wishlist}
                  alt="wishlist"
                  className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                />
                {tooltipOpen === idx && (
                  <div className="absolute left-9 top-0 bg-[#162c2a] text-white text-[10px] font-medium px-2 py-1 rounded-lg whitespace-nowrap shadow z-30">
                    {isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                  </div>
                )}
              </button>

              {/* Product image */}
              <div
  className={`w-full h-full overflow-hidden ${imgBg} cursor-pointer group`}
  onClick={() =>
    handleclickforfeaturedproduct(product.product_id, product)
  }
>
  <img
    src={
      product?.product_images?.image_url?.primary_image ||
      "/default.svg"
    }
    alt={product.name || "Product"}
    onError={(e) => {
      e.target.onerror = null;
      e.target.src = "/default.svg";
    }}
    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
  />
</div>
              <div className="w-full h-px bg-[#f1f5f5]" />

              {/* Product info */}
              <div
                className="px-3 sm:px-4 pt-3 pb-2 flex-1 flex flex-col gap-1.5 cursor-pointer"
                onClick={() =>
                  handleclickforfeaturedproduct(product.product_id, product)
                }
              >
                <h3 className="font-semibold text-[12px] sm:text-[13px] text-[#1e4a46] line-clamp-2 leading-snug group-hover:text-[#ff601e] transition-colors duration-200">
                  {product.name || "Unnamed Product"}
                </h3>

                {/* ── Price display ── */}
                <div className="flex items-baseline gap-2 flex-wrap">
                  {mrp ? (
                    <>
                      {/* Strike MRP only when there's a real discounted price */}
                      {showDiscount && (
                        <span className="text-[11px] text-[#8faba9] line-through">
                          ₹{mrp}
                        </span>
                      )}
                      {/* Final price — discounted or just mrp */}
                      <span className="font-bold text-[14px] sm:text-[15px] text-[#1e4a46]">
                        ₹{discountedPrice}
                      </span>
                      {/* % badge only when discount is real */}
                      {showDiscount && discountPct > 0 && (
                        <span
                          className={`text-[10px] font-semibold ${priceBadge}`}
                        >
                          {discountPct}% off
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-[12px] text-[#8faba9] font-medium">
                      0.00
                    </span>
                  )}
                </div>

                {isOutOfStock && (
                  <span className="self-start text-[10px] font-semibold text-red-500 bg-red-50 border border-red-100 rounded-lg px-2 py-0.5">
                    Out of Stock
                  </span>
                )}
              </div>

              {/* Cart action */}
              <div className="px-3 sm:px-4 pb-4 pt-1">
                {isMultiVariant ? (
                  <button
                    className="w-full h-10 bg-[#1e4a46] hover:bg-[#ff601e] text-white text-[11px] sm:text-[12px] font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-all duration-200 hover:shadow-[0_4px_14px_rgba(255,96,30,0.4)]"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!localStorage.getItem("authToken")) {
                        setShowLoginPopup(true);
                        return;
                      }
                      handleclickforfeaturedproduct(
                        product.product_id,
                        product,
                      );
                    }}
                  >
                    <img
                      src={addToCartIcon}
                      alt="Cart"
                      className="w-3.5 h-3.5 object-contain"
                    />
                    Choose Variant
                  </button>
                ) : cartQty > 0 ? (
                  <div className="flex items-center justify-between bg-[#eaf4f3] border border-[#d4ecea] rounded-xl h-10 px-3">
                    <button
                      onClick={(e) => handleDecrease(e, variant, product)}
                      className="w-7 h-7 flex items-center justify-center text-[#1e4a46] font-bold text-xl hover:bg-white rounded-lg transition-colors"
                    >
                      −
                    </button>
                    <span className="font-bold text-[14px] text-[#1e4a46] min-w-[24px] text-center">
                      {cartQty}
                    </span>
                    <button
                      onClick={(e) => handleIncrease(e, variant, product)}
                      className="w-7 h-7 flex items-center justify-center text-[#1e4a46] font-bold text-xl hover:bg-white rounded-lg transition-colors"
                    >
                      +
                    </button>
                  </div>
                ) : (
                  <button
                    className={`w-full h-10 text-white text-[11px] sm:text-[13px] font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-all duration-200 disabled:bg-[#c5d5d4] disabled:cursor-not-allowed ${btnCls}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!localStorage.getItem("authToken")) {
                        setShowLoginPopup(true);
                        return;
                      }
                      await handleAddToCart(product, variant, 1);
                    }}
                    disabled={isOutOfStock}
                  >
                    <img
                      src={addToCartIcon}
                      alt="Cart"
                      className="w-3.5 h-3.5 object-contain"
                    />
                    {isOutOfStock ? "Out of Stock" : "Add to Cart"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── View all CTA ── */}
      <div className="flex justify-center mt-10">
        <Link to="/products">
          <button className="flex items-center gap-2 bg-[#162c2a] hover:bg-[#ff601e] text-white font-semibold text-[13px] sm:text-[14px] px-8 h-11 rounded-xl transition-all duration-200 shadow-[0_3px_12px_rgba(22,44,42,0.25)] hover:shadow-[0_5px_20px_rgba(255,96,30,0.4)] hover:-translate-y-0.5">
            Explore All Medicines
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </Link>
      </div>

      {showLoginPopup && <Popup onClose={() => setShowLoginPopup(false)} />}
      <style>{`@keyframes fadeInUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </section>
  );
};

export default AllProducts_home;
